/**
 * useCaptureCamera — forked from vCIP_droom_4.0/src/media/useCaptureCamera.ts (S7-6 still-capture
 * camera machine) and extended for vSITE:
 *   • two acquisition PROFILES — `still` (1920×1080 ideal, the document/selfie grade) and `clip`
 *     (854×480 ideal @ 30 fps, 480p-first for MediaRecorder on India-mobile bandwidth);
 *   • optional AUDIO on clip acquisitions (SPEAK_NONCE challenges need the microphone);
 *   • the target lens is NAMED via cameraSurvey when enumeration can prove it, so a rear-camera
 *     request cannot be reinterpreted as a front-camera hint (the droom's switchFacing finding).
 *
 * Lifecycle correctness carried over verbatim: a re-acquisition stops the previous stream BEFORE
 * acquiring; all tracks stop on unmount; a stale getUserMedia resolution is detected by a
 * sequence counter; the <video> attaches via a CALLBACK ref. captureFrame() draws the RAW sensor
 * frame at native resolution → JPEG (0.92). No frames are logged or persisted here.
 *
 * US-03482 iPhone black-video RCA (2026-11-03):
 *   • G1 — ONE camera session per capture: the hook is owned by CaptureFlow (media/CaptureCameraContext.tsx), not by
 *     each shot. `start()` on a live stream with the same lens SWITCHES the profile on the running track
 *     (`applyConstraints`; a phone that refuses keeps its current quality) instead of a new getUserMedia — Chrome on
 *     iOS (WKWebView) re-prompts on every acquisition, and a prompt landing while the camera starts can leave WebKit
 *     with a live-but-frameless track. It re-acquires only for another lens, or when the microphone is needed and
 *     absent (once — a refused mic is not asked for again).
 *   • G3 — the frame watchdog: once a stream is live on a <video>, frames must arrive within FRAME_WATCHDOG_MS
 *     (requestVideoFrameCallback, else currentTime progress, with a non-zero videoWidth and an unmuted track). No
 *     frames → one automatic restart; still none → phase `stalled` (the screens say "tap to restart" and block the
 *     shutter) until frames arrive or `restart()` is tapped.
 *
 * Helm-T review of svapp #33 (2026-11-03):
 *   • a still that the running track cannot honour (refused, or its short side under the still floor after the
 *     switch) re-acquires ONCE at the still profile — a clip-grade 480p frame is never taken as a document photo;
 *   • F10: the microphone is ON only on steps that record audio — the shared session's audio track is disabled
 *     (`enabled = false`, no re-prompt) on still steps;
 *   • the watchdog reopens on demand (`recheckFrames`, an EMPTY take, a retake) and on a track's `ended`, pauses while
 *     the page is hidden or the track muted, stops on detach; `framesSeen` gates the shutters;
 *   • `stop()` resets the phase, so a return from review starts the camera once — no second getUserMedia, no primer;
 *   • a getUserMedia failure while the camera permission reads `granted` is not "denied" (another app holds the camera,
 *     or the OS refused it): it is reported as the os-level block, whose words say to close other apps and retry.
 *
 * US-03482 round 3 (F-7 / P-1, owner 2026-11-03: a camera that freezes after it starts must be caught):
 *   • A4 — the watch runs for the WHOLE step, not only its first frames: once frames have flowed, about FREEZE_WATCH_MS
 *     with no new frame, or with the track muted, while the page is visible, is a stall — phase `stalled` (the same card,
 *     "Restart camera"), never an automatic restart. A hidden page is not a stall (nothing is drawn for it). The track's
 *     `unmute` reopens the window: the clock starts again from the unmute.
 *   • `setRecording(true)` while a clip records: the camera is NEVER restarted under it — a first-frames window that
 *     runs out, or a track that ends, goes straight to `stalled`, and the clip screen ends the take as a retake; `restart()`
 *     does nothing until the recording has stopped.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { surveyCameras } from './cameraSurvey';
import { detectBrowser, detectWebview, queryPermissionState, recoveryFor, type BrowserFamily } from './permissions';

export type CaptureFacing = 'environment' | 'user';
export type CaptureProfile = 'still' | 'clip';

export type CaptureCameraPhase =
  | 'checking'
  | 'primer'
  | 'starting'
  | 'live'
  | 'denied'
  | 'os-denied'
  | 'no-device'
  | 'webview'
  | 'unsupported'
  /** G3: live, but no frames after one automatic restart — the screens offer "tap to restart". */
  | 'stalled';

const JPEG_QUALITY = 0.92;

/** G3: how long a live stream on a <video> may go without a frame before the watchdog acts. */
export const FRAME_WATCHDOG_MS = 3_000;
/** A4: once frames have flowed, how long without a new one (or muted), page visible, before the step reads stalled. */
export const FREEZE_WATCH_MS = 10_000;
const FRAME_POLL_MS = 250;
/** The short side a still must be able to reach on the running track; below it the still re-acquires (480p is a clip). */
export const STILL_FLOOR_SHORT_SIDE = 720;

export const PROFILE_VIDEO: Record<CaptureProfile, MediaTrackConstraints> = {
  still: { width: { ideal: 1920 }, height: { ideal: 1080 } },
  clip: { width: { ideal: 854 }, height: { ideal: 480 }, frameRate: { ideal: 30 } },
};

export interface StartOptions {
  readonly profile: CaptureProfile;
  /** Acquire a microphone with the camera (the flow asks for the session's mic up front when the script has a clip). */
  readonly audio: boolean;
  /** F10: whether THIS step's microphone is on; defaults to `audio`. Off = the audio track is disabled, not stopped. */
  readonly micOn?: boolean;
}

export interface CaptureCameraApi {
  readonly phase: CaptureCameraPhase;
  readonly browser: BrowserFamily;
  readonly preGranted: boolean;
  readonly requestedFacing: CaptureFacing | null;
  readonly activeFacing: CaptureFacing | null;
  /** True when the live stream carries an audio track (mic granted). */
  readonly hasAudio: boolean;
  /** Whether enumeration proved more than one lens (drives the flip affordance). */
  readonly multiCamera: boolean | null;
  readonly detail: string | null;
  readonly recoverySteps: readonly string[];
  readonly attachVideo: (el: HTMLVideoElement | null) => void;
  readonly getStream: () => MediaStream | null;
  readonly getVideo: () => HTMLVideoElement | null;
  readonly start: (facing: CaptureFacing, opts: StartOptions) => void;
  readonly stop: () => void;
  /** G3: re-acquire the current lens and profile now (the "tap to restart" on a stalled camera). */
  readonly restart: () => void;
  /** G3: reopen the frame-watchdog window (an EMPTY take, a retake). */
  readonly recheckFrames: () => void;
  /** G3: a frame has arrived since the current window opened — the shutters wait for it. */
  readonly framesSeen: boolean;
  /** A4: a clip is recording — the camera is never restarted under it (a stall ends the take instead). */
  readonly setRecording: (on: boolean) => void;
  readonly captureFrame: () => Promise<Blob | null>;
}

export function useCaptureCamera(enabled: boolean): CaptureCameraApi {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browser = detectBrowser(ua);
  const isWebview = detectWebview(ua);

  const [phase, setPhase] = useState<CaptureCameraPhase>('checking');
  const [preGranted, setPreGranted] = useState(false);
  const [requestedFacing, setRequestedFacing] = useState<CaptureFacing | null>(null);
  const [activeFacing, setActiveFacing] = useState<CaptureFacing | null>(null);
  const [hasAudio, setHasAudio] = useState(false);
  const [multiCamera, setMultiCamera] = useState<boolean | null>(null);
  const [detail, setDetail] = useState<string | null>(null);

  const disposed = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);
  const videoElRef = useRef<HTMLVideoElement | null>(null);
  const startSeq = useRef(0);
  // G1: what the running stream was acquired / switched for, so a same-lens start() can reuse it.
  const phaseRef = useRef<CaptureCameraPhase>('checking');
  phaseRef.current = phase;
  const current = useRef<{ facing: CaptureFacing; profile: CaptureProfile; audio: boolean } | null>(null);
  const audioRefused = useRef(false);
  // G3: bumped on every acquisition and every <video> attach — each opens a fresh frame-watchdog window.
  const [watchGen, setWatchGen] = useState(0);
  const autoRestarts = useRef(0);
  const [framesSeen, setFramesSeen] = useState(false);
  const stillRetried = useRef(false);
  // A4: set by the clip screen while MediaRecorder runs — no automatic restart, no restart() under a recording
  const recordingRef = useRef(false);
  const setRecording = useCallback((on: boolean): void => {
    recordingRef.current = on;
  }, []);

  const stopTracks = useCallback(() => {
    const s = streamRef.current;
    if (s) for (const t of s.getTracks()) t.stop();
    streamRef.current = null;
    const el = videoElRef.current;
    if (el) el.srcObject = null;
    current.current = null;
  }, []);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
      startSeq.current += 1;
      stopTracks();
    };
  }, [stopTracks]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setPhase('unsupported');
      return;
    }
    if (isWebview) {
      setPhase('webview');
      return;
    }
    let cancelled = false;
    (async () => {
      const cam = await queryPermissionState('camera');
      if (cancelled || disposed.current) return;
      if (cam === 'denied') {
        setPhase('denied');
        return;
      }
      setPreGranted(cam === 'granted');
      // Back from review (the session was stopped): with the permission already granted, start the camera once,
      // straight away — no primer flash, and no second getUserMedia from a primer effect racing it.
      const ask = lastAsk.current;
      if (cam === 'granted' && ask && phaseRef.current === 'checking') {
        void startRef.current(ask.facing, ask.opts);
        return;
      }
      if (phaseRef.current === 'checking') setPhase('primer');
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, isWebview]);

  const attachVideo = useCallback((el: HTMLVideoElement | null) => {
    videoElRef.current = el;
    if (el && streamRef.current) {
      el.srcObject = streamRef.current;
      void el.play().catch(() => {});
    }
    // A new element opens a fresh watchdog window; a detach clears the old one's interval.
    setFramesSeen(false);
    setWatchGen((g) => g + 1);
  }, []);

  const acquire = useCallback(
    async (facing: CaptureFacing, opts: StartOptions): Promise<MediaStream> => {
      const md = navigator.mediaDevices;
      const survey = await surveyCameras(facing);
      if (survey.cameraCount > 0) setMultiCamera(survey.cameraCount > 1);
      const base = PROFILE_VIDEO[opts.profile];
      const audio = opts.audio ? { echoCancellation: true, noiseSuppression: true } : false;
      // Rung A: an exact deviceId the browser cannot reinterpret; rung B: the facing hint.
      if (survey.targetDeviceId) {
        try {
          return await md.getUserMedia({ audio, video: { ...base, deviceId: { exact: survey.targetDeviceId } } });
        } catch {
          /* busy / withdrawn — fall through to the facing hint */
        }
      }
      try {
        return await md.getUserMedia({ audio, video: { ...base, facingMode: { ideal: facing } } });
      } catch (e) {
        // A microphone refusal must not cost the representative their camera: retry video-only.
        if (opts.audio) {
          return md.getUserMedia({ audio: false, video: { ...base, facingMode: { ideal: facing } } });
        }
        throw e;
      }
    },
    [],
  );

  /** F10: the microphone follows the step — on for steps that record audio, off (disabled, never stopped) otherwise. */
  const setMic = useCallback((on: boolean): void => {
    for (const t of streamRef.current?.getAudioTracks() ?? []) t.enabled = on;
  }, []);

  /**
   * G1: switch the running track's quality. `cur.profile` changes only on success. A still that the track cannot honour
   * (refused, or a short side under STILL_FLOOR_SHORT_SIDE after the switch) asks for a re-acquisition at the still
   * profile — once per stream; a refused clip switch keeps the current quality.
   */
  const applyProfile = useCallback(async (profile: CaptureProfile): Promise<'ok' | 'reacquire'> => {
    const cur = current.current;
    const track = streamRef.current?.getVideoTracks()[0];
    if (!cur || !track || cur.profile === profile) return 'ok';
    let refused = typeof track.applyConstraints !== 'function';
    if (!refused) {
      try {
        await track.applyConstraints(PROFILE_VIDEO[profile]);
      } catch {
        refused = true;
      }
    }
    if (profile === 'still') {
      const st = track.getSettings?.() ?? {};
      const short = Math.min(st.width ?? 0, st.height ?? 0);
      const belowFloor = short > 0 && short < STILL_FLOOR_SHORT_SIDE;
      if (refused || belowFloor) return stillRetried.current ? 'ok' : 'reacquire';
    } else if (refused) {
      return 'ok'; // keep the quality the camera is already giving
    }
    if (current.current === cur) cur.profile = profile;
    return 'ok';
  }, []);

  const start = useCallback(
    async (facing: CaptureFacing, opts: StartOptions, force = false): Promise<void> => {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        setPhase('unsupported');
        return;
      }
      // G1: reuse the running session when it can serve this shot.
      const s = streamRef.current;
      const cur = current.current;
      const videoLive = s?.getVideoTracks()[0]?.readyState === 'live';
      if (!force && s && cur && videoLive && (phaseRef.current === 'live' || phaseRef.current === 'stalled') && cur.facing === facing) {
        const needsMic = opts.audio && s.getAudioTracks().length === 0 && !audioRefused.current;
        if (!needsMic) {
          setMic(opts.micOn ?? opts.audio);
          if ((await applyProfile(opts.profile)) === 'ok') return;
          stillRetried.current = true; // the one still re-acquisition for this stream
        }
      }
      const seq = ++startSeq.current;
      stopTracks();
      setRequestedFacing(facing);
      setActiveFacing(null);
      setDetail(null);
      setPhase('starting');
      try {
        const stream = await acquire(facing, opts);
        if (seq !== startSeq.current || disposed.current) {
          for (const t of stream.getTracks()) t.stop();
          return;
        }
        streamRef.current = stream;
        const gotAudio = stream.getAudioTracks().length > 0;
        if (opts.audio && !gotAudio) audioRefused.current = true;
        // The one still re-acquisition is per STREAM: a stream acquired for a still that reaches the floor proves the
        // lens can, so a later switch back from a clip may re-acquire again; one that cannot reach it even when asked
        // never will — that lens is not re-acquired for the floor again.
        if (opts.profile === 'still') {
          const st = stream.getVideoTracks()[0]?.getSettings?.() ?? {};
          const short = Math.min(st.width ?? 0, st.height ?? 0);
          stillRetried.current = short > 0 && short < STILL_FLOOR_SHORT_SIDE;
        } else {
          stillRetried.current = false;
        }
        current.current = { facing, profile: opts.profile, audio: gotAudio };
        setMic(opts.micOn ?? opts.audio);
        const track = stream.getVideoTracks()[0];
        const reported = track?.getSettings().facingMode;
        setActiveFacing(reported === 'user' || reported === 'environment' ? reported : null);
        setHasAudio(gotAudio);
        const el = videoElRef.current;
        if (el) {
          el.srcObject = stream;
          void el.play().catch(() => {});
        }
        setPhase('live');
        setFramesSeen(false);
        setWatchGen((g) => g + 1);
      } catch (e) {
        if (seq !== startSeq.current || disposed.current) return;
        const name = e instanceof DOMException ? e.name : '';
        if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
          setDetail('sv.cam.nodevice.hookDetail');
          setPhase('no-device');
          return;
        }
        const after = await queryPermissionState('camera');
        if (seq !== startSeq.current || disposed.current) return;
        // `granted` + a failure is not the customer's "no": another app holds the camera, or the OS refused it.
        setPhase(after === 'denied' ? 'denied' : 'os-denied');
      }
    },
    [acquire, applyProfile, setMic, stopTracks],
  );
  const startRef = useRef(start);
  startRef.current = start;

  const stop = useCallback(() => {
    startSeq.current += 1;
    stopTracks();
    setActiveFacing(null);
    setHasAudio(false);
    setFramesSeen(false);
    // Not "live" any more: the next start (a return from review) acquires once, from the enabled effect.
    if (phaseRef.current !== 'checking') {
      phaseRef.current = 'checking';
      setPhase('checking');
    }
  }, [stopTracks]);

  /** The last lens / profile / mic a start asked for — what a restart re-acquires. */
  const lastAsk = useRef<{ facing: CaptureFacing; opts: StartOptions } | null>(null);
  const startRemembered = useCallback(
    (facing: CaptureFacing, opts: StartOptions): void => {
      lastAsk.current = { facing, opts };
      void start(facing, opts);
    },
    [start],
  );
  const restart = useCallback((): void => {
    const ask = lastAsk.current;
    if (!ask || recordingRef.current) return; // A4: never under a recording clip — the take is ended first
    autoRestarts.current = 0;
    void start(ask.facing, ask.opts, true);
  }, [start]);
  const recheckFrames = useCallback((): void => {
    autoRestarts.current = 0;
    setFramesSeen(false);
    setWatchGen((g) => g + 1);
  }, []);

  // G3 — the frame watchdog. A window opens on every acquisition, every <video> attach and every `recheckFrames`;
  // frames must arrive in it. Time does not count while the page is hidden or the track is muted.
  // A4 — and once they have, the same interval keeps watching for the rest of the step (see the file comment).
  useEffect(() => {
    if (phase !== 'live' && phase !== 'stalled') return undefined;
    const stream = streamRef.current;
    const el = videoElRef.current;
    if (!stream || !el) return undefined; // detached: the previous window's interval is already cleared
    const track = stream.getVideoTracks()[0];
    let frames = 0;
    let lastTime = el.currentTime;
    let cancelled = false;
    type RvfcVideo = HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number; cancelVideoFrameCallback?: (h: number) => void };
    const v = el as RvfcVideo;
    let rvfc: number | null = null;
    // requestVideoFrameCallback, ELSE currentTime progress: where the browser reports the frames it presents, they are the
    // only proof of a picture — a <video> playing a stream that also carries the session's microphone advances currentTime
    // with the audio while the picture is frozen (A4), and a frozen picture must never read as flowing
    const presents = typeof v.requestVideoFrameCallback === 'function';
    const onFrame = (): void => {
      if (cancelled) return;
      frames += 1;
      rvfc = v.requestVideoFrameCallback ? v.requestVideoFrameCallback(onFrame) : null;
    };
    if (presents) rvfc = v.requestVideoFrameCallback!(onFrame);
    let waited = 0;
    let last = Date.now();
    // A4: whether this window's first frames have arrived (from then on the freeze watch runs), and for how long no new
    // frame has (muted or not; never counted while the page is hidden)
    let flowed = false;
    let counted = 0;
    let idle = 0;
    // A track that ends opens a fresh window (no frames → the automatic restart) — but never under a recording clip.
    const onEnded = (): void => {
      if (cancelled) return;
      if (recordingRef.current) setPhase('stalled');
      else setWatchGen((g) => g + 1);
    };
    // A4: an unmute reopens the window — the unmuted track gets its full time again
    const onUnmute = (): void => {
      waited = 0;
      idle = 0;
    };
    track?.addEventListener?.('ended', onEnded);
    track?.addEventListener?.('unmute', onUnmute);
    const poll = window.setInterval(() => {
      const now = Date.now();
      const step = now - last;
      last = now;
      const video = videoElRef.current;
      if (!video || video !== el) return;
      if (!presents && video.currentTime !== lastTime) {
        frames += 1;
        lastTime = video.currentTime;
      }
      const fresh = frames > counted;
      counted = frames;
      const muted = track?.muted ?? false;
      const hidden = typeof document !== 'undefined' && document.hidden;
      if (flowed) {
        // A4 — the rest of the step: new frames keep it live; ~FREEZE_WATCH_MS without one (or muted), visible, is a stall
        if (hidden) return; // a hidden page is not a stall
        if (fresh && !muted && track?.readyState === 'live') {
          idle = 0;
          if (phaseRef.current === 'stalled') setPhase('live');
          return;
        }
        idle += step;
        if (idle >= FREEZE_WATCH_MS && phaseRef.current !== 'stalled') setPhase('stalled'); // no automatic restart
        return;
      }
      const flowing = frames > 0 && video.videoWidth > 0 && video.readyState >= 2 && !muted && track?.readyState === 'live';
      if (flowing) {
        flowed = true;
        idle = 0;
        autoRestarts.current = 0;
        setFramesSeen(true);
        if (phaseRef.current === 'stalled') setPhase('live');
        return;
      }
      // Paused while the page is hidden or the track muted (a call, the OS taking the camera for a moment).
      if (hidden || muted) return;
      waited += step;
      if (waited < FRAME_WATCHDOG_MS || phaseRef.current === 'stalled') return;
      const ask = lastAsk.current;
      if (autoRestarts.current < 1 && ask && !recordingRef.current) {
        window.clearInterval(poll);
        autoRestarts.current += 1;
        void start(ask.facing, ask.opts, true); // one automatic restart
      } else {
        setPhase('stalled'); // the watch goes on in the new phase: frames that arrive bring it back
      }
    }, FRAME_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
      track?.removeEventListener?.('ended', onEnded);
      track?.removeEventListener?.('unmute', onUnmute);
      if (rvfc !== null && typeof v.cancelVideoFrameCallback === 'function') v.cancelVideoFrameCallback(rvfc);
    };
  }, [phase, watchGen, start]);

  const getStream = useCallback((): MediaStream | null => streamRef.current, []);
  const getVideo = useCallback((): HTMLVideoElement | null => videoElRef.current, []);

  const captureFrame = useCallback(async (): Promise<Blob | null> => {
    const el = videoElRef.current;
    if (!el || el.readyState < 2) return null;
    const w = el.videoWidth;
    const h = el.videoHeight;
    if (!w || !h) return null;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(el, 0, 0, w, h);
    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY);
    });
  }, []);

  return {
    phase,
    browser,
    preGranted,
    requestedFacing,
    activeFacing,
    hasAudio,
    multiCamera,
    detail,
    recoverySteps: phase === 'os-denied' ? recoveryFor(browser, true) : recoveryFor(browser, false),
    attachVideo,
    getStream,
    getVideo,
    start: startRemembered,
    stop,
    restart,
    recheckFrames,
    framesSeen,
    setRecording,
    captureFrame,
  };
}
